// Lote V-9 — Web shop page. Lista o catálogo LeyroSpot + historial de pedidos.
//
// Mesmo fluxo que mobile/screens/Shop.tsx:
//   1. GET /shop/products + /shop/orders
//   2. Botón "Mercar" abre Stripe Checkout (POST /shop/checkout-session)
//   3. Stripe redirixe a /shop/success (ou /shop/cancel)

import { Package, ShoppingBag, Truck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import {
  listMyHardwareOrders,
  listShopProducts,
  startShopCheckout,
  type HardwareOrder,
  type HardwareSku,
  type ShopProduct,
} from '../api/shop';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

function formatEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('gl-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const VALID_SKUS: readonly HardwareSku[] = ['KIT_10', 'KIT_50'];

export default function ShopPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuthContext();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [orders, setOrders] = useState<HardwareOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busySku, setBusySku] = useState<HardwareSku | null>(null);
  // Lote V-16 — auto-trigger checkout cando o usuario chega dende a
  // landing (chain.leyro.es/) cun `?sku=KIT_10|KIT_50`. Disparado só
  // unha vez por sesión para evitar bucles.
  const autoTriggeredRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    void Promise.all([listShopProducts(), listMyHardwareOrders()])
      .then(([p, o]) => {
        if (cancelled) return;
        setProducts(p);
        setOrders(o);
      })
      .catch((err) => {
        if (!cancelled) addToast(describeAxiosError(err, t('shop.errors.load')), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, addToast, t]);

  // Lote V-16 — auto-trigger checkout cando `?sku=` está presente, hai
  // accessToken, os productos cargaron e o SKU é válido. Limpa o query
  // string despois para non disparar de novo nun refresh. Disparado só
  // unha vez por montaxe (ref garante idempotencia).
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (loading || busySku) return;
    if (!accessToken) return;
    const sku = searchParams.get('sku');
    if (!sku || !VALID_SKUS.includes(sku as HardwareSku)) return;
    autoTriggeredRef.current = true;
    setSearchParams({}, { replace: true });
    (async () => {
      setBusySku(sku as HardwareSku);
      try {
        const url = await startShopCheckout(sku as HardwareSku);
        window.location.assign(url);
      } catch (err) {
        addToast(describeAxiosError(err, t('shop.errors.checkout')), 'error');
        setBusySku(null);
      }
    })();
  }, [loading, accessToken, busySku, searchParams, setSearchParams, addToast, t]);

  if (!accessToken) {
    // Preserva o `?sku=` ao redirixir ao login para que o auto-trigger
    // funcione despois de loguearse.
    const next = `/marketplace/shop${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return <Navigate to={`/marketplace/login?next=${encodeURIComponent(next)}`} replace />;
  }

  const handleBuy = async (sku: HardwareSku) => {
    setBusySku(sku);
    try {
      const url = await startShopCheckout(sku);
      window.location.assign(url);
    } catch (err) {
      addToast(describeAxiosError(err, t('shop.errors.checkout')), 'error');
      setBusySku(null);
    }
  };

  return (
    <ContentPage title={t('shop.title')} intro={t('shop.subtitle')} category={t('shop.category')}>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> {t('common.loading')}
        </div>
      )}

      {!loading && (
        <>
          <section className="not-prose grid gap-4 md:grid-cols-2">
            {products.map((p) => (
              <div
                key={p.sku}
                className="border border-border rounded-2xl p-6 bg-white flex flex-col"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-rauxa/10 text-rauxa flex items-center justify-center shrink-0">
                    <Package size={22} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-dark">{p.label}</h3>
                    <p className="text-sm text-muted mt-1 leading-relaxed">{p.description}</p>
                  </div>
                </div>
                <div className="flex items-end justify-between mt-auto">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted font-medium">
                      {t('shop.price')}
                    </p>
                    <p className="text-2xl font-bold text-dark">{formatEuros(p.amount_cents)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleBuy(p.sku)}
                    disabled={!p.available || busySku !== null}
                    className="inline-flex items-center gap-2 bg-rauxa text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-rauxa/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {busySku === p.sku ? (
                      <>
                        <Spinner size={14} /> {t('shop.opening')}
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={16} /> {t('shop.buy')}
                      </>
                    )}
                  </button>
                </div>
                {!p.available && (
                  <p className="text-xs text-amber-700 mt-3">{t('shop.unavailable')}</p>
                )}
              </div>
            ))}
          </section>

          {orders.length > 0 && (
            <section className="not-prose mt-10">
              <h2 className="text-xl font-bold text-dark mb-4 flex items-center gap-2">
                <Truck size={20} /> {t('shop.ordersHeading')}
              </h2>
              <ul className="border border-border rounded-2xl bg-white divide-y divide-border overflow-hidden">
                {orders.map((o) => (
                  <li key={o.id} className="p-4 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-dark">{o.product_label}</p>
                      <p className="text-xs text-muted mt-0.5">
                        {formatDate(o.created_at)} · {formatEuros(o.amount_cents)}
                      </p>
                      {o.tracking_number && (
                        <p className="text-xs text-muted mt-0.5">
                          {t('shop.trackingLabel')}: {o.tracking_number}
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-[11px] uppercase tracking-wide font-bold px-2.5 py-1 rounded-full ${
                        o.status === 'SHIPPED' || o.status === 'DELIVERED'
                          ? 'bg-green-100 text-green-800'
                          : o.status === 'PENDING_FULFILLMENT'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-subtle text-muted'
                      }`}
                    >
                      {t(`shop.status.${o.status}`, { defaultValue: o.status })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </ContentPage>
  );
}
